import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Palette, AlertTriangle } from 'lucide-react';
import { CreateActivityTypeData } from '../services/activityApi';
import { apiClient } from '../utils/api';
import { useToast } from '../components/Toast';

export function EmployeeActivityTypeCreatePage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  
  const [formData, setFormData] = useState<CreateActivityTypeData>({
    name: '',
    description: '',
    defaultColor: '#3B82F6',
    isBlocking: true,
    defaultVisibility: 'INTERNAL',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  const handleInputChange = (field: keyof CreateActivityTypeData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name) {
      setError('Le nom est requis');
      return;
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(formData.defaultColor || '#3B82F6')) {
      setError('La couleur doit être au format hexadécimal (ex: #3B82F6)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      
      const response = await apiClient('/api/planning/employee-activity-types', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        showToast('Type d\'activité employé créé avec succès', 'success');
        setTimeout(() => {
          navigate('/planning/employee-activity-types');
        }, 300);
      } else {
        const message = data.message || 'Échec de la création du type d\'activité employé';
        setError(message);
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
        showToast(message, 'error', 7000);
      }
    } catch (error: any) {
      console.error('Create employee activity type error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <ToastComponent />
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/planning/employee-activity-types')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Créer un Type d'Activité Employé</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Palette className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom du Type d'Activité Employé *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input-field"
                placeholder="Ex: Pause, Formation, Réunion..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Description du type d'activité employé..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Couleur par Défaut *
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.defaultColor || '#3B82F6'}
                    onChange={(e) => handleInputChange('defaultColor', e.target.value)}
                    className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.defaultColor || '#3B82F6'}
                    onChange={(e) => handleInputChange('defaultColor', e.target.value)}
                    className="input-field flex-1"
                    placeholder="#3B82F6"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Format hexadécimal (ex: #3B82F6)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Visibilité par Défaut *
                </label>
                <select
                  value={formData.defaultVisibility || 'INTERNAL'}
                  onChange={(e) => handleInputChange('defaultVisibility', e.target.value as any)}
                  className="input-field"
                  required
                >
                  <option value="INTERNAL">Interne (backoffice uniquement)</option>
                  <option value="FRONTOFFICE">Public (visible aux clients)</option>
                  <option value="PRIVATE">Privé (employé + admins)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isBlocking !== false}
                  onChange={(e) => handleInputChange('isBlocking', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-textPrimary">
                  Bloque les réservations par défaut
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Si coché, ce type d'activité empêchera les réservations pendant sa durée
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/planning/employee-activity-types')}
            className="btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Création...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Créer</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
